// src/components/pages/ProjectChat.jsx  (people.eilanApplication)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Input, Button, Avatar, Typography, Divider,
  Badge, Tooltip, message as AntMessage, Tag,
} from 'antd';
import {
  SendOutlined, PaperClipOutlined, SmileOutlined,
  FileOutlined, UserOutlined, CloseOutlined,
  ArrowDownOutlined, BoldOutlined, ItalicOutlined,
  StrikethroughOutlined, CodeOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import EmojiPicker from 'emoji-picker-react';
import axios from 'axios';
import webSocketService from '../../components/WebSocketService';

// Sound imports
let mentionSound, notificationSound;
try { mentionSound      = require('../../assets/audio/mention.mp3');      } catch { mentionSound      = null; }
try { notificationSound = require('../../assets/audio/notification.mp3'); } catch { notificationSound = null; }

const { Text } = Typography;

const API_BASE_URL = 'https://api.eilandigital.com/eilanApplication-0.0.1-SNAPSHOT/api/project-chat';

// ─────────────────────────────────────────────────────────────────────────────
// MARK PROJECT AS READ
// Updates the backend last_read pointer so unread badge counts are correct
// after a page refresh. Called automatically when messages finish loading.
// ─────────────────────────────────────────────────────────────────────────────
async function markProjectChatAsRead(projectId, employeeId, lastMessageId) {
  if (!projectId || !employeeId || !lastMessageId) return;
  try {
    await fetch(
      `${API_BASE_URL}/unread/mark-read?projectId=${projectId}&employeeId=${employeeId}&lastMessageId=${lastMessageId}`,
      { method: 'POST' }
    );
  } catch (err) {
    console.warn('markProjectChatAsRead failed (non-fatal):', err.message);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SOUND
// ─────────────────────────────────────────────────────────────────────────────
const playSound = (src, volume = 0.5) => {
  if (!src) return;
  try {
    const audio = new Audio(typeof src === 'string' ? src : src.default || src);
    audio.volume = volume;
    audio.play().catch(() => {});
  } catch { /* ignore */ }
};

// ─────────────────────────────────────────────────────────────────────────────
// ATTACHMENT HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const isImageFile = (fileName, fileType) => {
  if (fileType && fileType.startsWith('image/')) return true;
  if (!fileName) return false;
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(fileName);
};

const isVideoFile = (fileName, fileType) => {
  if (fileType && fileType.startsWith('video/')) return true;
  if (!fileName) return false;
  return /\.(mp4|webm|ogg|mov|avi)$/i.test(fileName);
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(2) + ' MB';
};

const handleDownload = async (e, fileUrl, fileName) => {
  e.preventDefault();
  e.stopPropagation();
  try {
    const response = await fetch(fileUrl);
    const blob     = await response.blob();
    const url      = URL.createObjectURL(blob);
    const a        = document.createElement('a');
    a.href         = url;
    a.download     = fileName || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    window.open(fileUrl, '_blank');
  }
};

const DownloadBtn = ({ fileUrl, fileName }) => (
  <Tooltip title="Download">
    <button
      onClick={(e) => handleDownload(e, fileUrl, fileName)}
      style={{
        background: 'rgba(0,0,0,0.06)', border: 'none',
        borderRadius: '50%', width: 28, height: 28,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0, transition: 'background 0.15s',
        color: '#444',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.14)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.06)'}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
    </button>
  </Tooltip>
);

const Attachment = ({ file }) => {
  if (!file) return null;
  const { fileUrl, fileName, fileSize, fileType } = file;
  if (isImageFile(fileName, fileType)) {
    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <a href={fileUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            <img src={fileUrl} alt={fileName}
              style={{ maxWidth: 260, maxHeight: 200, borderRadius: 8, display: 'block', objectFit: 'cover', border: '1px solid rgba(0,0,0,0.08)', cursor: 'pointer' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
          <div style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(255,255,255,0.92)', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>
            <DownloadBtn fileUrl={fileUrl} fileName={fileName} />
          </div>
        </div>
      </div>
    );
  }
  if (isVideoFile(fileName, fileType)) {
    return (
      <div style={{ marginTop: 4 }}>
        <video src={fileUrl} controls style={{ maxWidth: 260, maxHeight: 180, borderRadius: 8, display: 'block', background: '#000' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
          {fileName && <div style={{ fontSize: 11, color: '#888' }}>{fileName} {fileSize ? `(${formatFileSize(fileSize)})` : ''}</div>}
          <DownloadBtn fileUrl={fileUrl} fileName={fileName} />
        </div>
      </div>
    );
  }
  const ext = fileName ? fileName.split('.').pop().toUpperCase() : 'FILE';
  const extColors = { PDF: '#ff4d4f', DOC: '#1890ff', DOCX: '#1890ff', XLS: '#52c41a', XLSX: '#52c41a', PPT: '#fa8c16', PPTX: '#fa8c16', TXT: '#666' };
  const color = extColors[ext] || '#666';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(0,0,0,0.04)', borderRadius: 8, padding: '8px 12px', marginTop: 4, border: '1px solid rgba(0,0,0,0.08)', maxWidth: 280 }}>
      <a href={fileUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
        style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
        <div style={{ width: 36, height: 36, borderRadius: 6, background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0, letterSpacing: 0.5 }}>
          {ext.slice(0, 4)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#262626', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{fileName || 'File'}</div>
          {fileSize > 0 && <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{formatFileSize(fileSize)}</div>}
        </div>
      </a>
      <DownloadBtn fileUrl={fileUrl} fileName={fileName} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────────────────────────────────────
const getInitials = (name) => {
  if (!name) return 'NA';
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

const generateColor = (name) => {
  const colors = ['#ff6b6b','#4ecdc4','#45b7d1','#96ceb4','#feca57','#ff9ff3','#54a0ff','#5f27cd'];
  const hash   = (name || '').split('').reduce((a, b) => a + b.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// ─────────────────────────────────────────────────────────────────────────────
// DATE GROUPING
// ─────────────────────────────────────────────────────────────────────────────
const parseMoment = (dateVal) => {
  if (!dateVal) return moment();
  if (Array.isArray(dateVal)) {
    return moment({ year: dateVal[0], month: dateVal[1] - 1, date: dateVal[2], hour: dateVal[3] || 0, minute: dateVal[4] || 0, second: dateVal[5] || 0 });
  }
  return moment(dateVal);
};

const getDateLabel = (dateVal) => {
  const date  = parseMoment(dateVal);
  if (!date.isValid()) return 'Today';
  const today = moment().startOf('day');
  const diff  = today.diff(date.clone().startOf('day'), 'days');
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 0)  return 'Today';
  return date.format('dddd, DD MMM YYYY');
};

const groupMessagesByDate = (messages) => {
  const groups = [];
  let currentLabel = null;
  messages.forEach((msg) => {
    const label = getDateLabel(msg.createdDate);
    if (label !== currentLabel) { groups.push({ type: 'date-header', label }); currentLabel = label; }
    groups.push({ type: 'message', msg });
  });
  return groups;
};

// ─────────────────────────────────────────────────────────────────────────────
// URL AUTO-DETECTION
// ─────────────────────────────────────────────────────────────────────────────
const linkifyText = (text) => {
  if (!text) return null;
  const result = [];
  let last = 0;
  let match;
  const rx = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+\.[^\s<>"']+)/gi;
  while ((match = rx.exec(text)) !== null) {
    if (match.index > last) result.push(text.slice(last, match.index));
    const url  = match[0];
    const href = url.startsWith('http') ? url : `https://${url}`;
    result.push(<a key={match.index} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#1890ff', textDecoration: 'underline', wordBreak: 'break-all' }}>{url}</a>);
    last = match.index + match[0].length;
  }
  if (last < text.length) result.push(text.slice(last));
  return result.length === 0 ? text : result;
};

// ─────────────────────────────────────────────────────────────────────────────
// INLINE FORMATTER
// ─────────────────────────────────────────────────────────────────────────────
const parseInlineFormatting = (text, baseKey = 0) => {
  if (!text) return null;
  const patterns = [
    { regex: /\*\*([\s\S]*?)\*\*/g, render: (c, k) => <strong key={k} style={{ fontWeight: 700 }}>{c}</strong> },
    { regex: /\*([\s\S]*?)\*/g,     render: (c, k) => <em key={k} style={{ fontStyle: 'italic' }}>{c}</em> },
    { regex: /~~([\s\S]*?)~~/g,     render: (c, k) => <span key={k} style={{ textDecoration: 'line-through' }}>{c}</span> },
    { regex: /`([^`]+)`/g,          render: (c, k) => <code key={k} style={{ background: 'rgba(0,0,0,0.1)', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace', fontSize: '0.9em' }}>{c}</code> },
  ];
  const tokens = [];
  patterns.forEach(({ regex, render }) => {
    regex.lastIndex = 0;
    let m;
    while ((m = regex.exec(text)) !== null) tokens.push({ start: m.index, end: m.index + m[0].length, content: m[1], render });
  });
  if (!tokens.length) return linkifyText(text);
  tokens.sort((a, b) => a.start - b.start);
  const used = [];
  tokens.forEach(t => { if (!used.some(u => t.start < u.end && t.end > u.start)) used.push(t); });
  const result = [];
  let last = 0;
  let k = baseKey * 10000;
  used.forEach(token => {
    if (token.start > last) {
      const linked = linkifyText(text.slice(last, token.start));
      (Array.isArray(linked) ? linked : [linked]).forEach(l => result.push(React.isValidElement(l) ? React.cloneElement(l, { key: k++ }) : <span key={k++}>{l}</span>));
    }
    result.push(token.render(token.content, k++));
    last = token.end;
  });
  if (last < text.length) {
    const linked = linkifyText(text.slice(last));
    (Array.isArray(linked) ? linked : [linked]).forEach(l => result.push(React.isValidElement(l) ? React.cloneElement(l, { key: k++ }) : <span key={k++}>{l}</span>));
  }
  return result;
};

// ─────────────────────────────────────────────────────────────────────────────
// FULL MESSAGE RENDERER (code blocks, blockquotes, headings, lists, etc.)
// ─────────────────────────────────────────────────────────────────────────────
const parseFormattingSegment = (text, baseKey = 0) => {
  if (!text) return null;
  let k = baseKey * 100000;
  const elements = [];
  const segments = [];
  const codeBlockRx = /```([\s\S]*?)```/g;
  let last = 0; let m;
  while ((m = codeBlockRx.exec(text)) !== null) {
    if (m.index > last) segments.push({ type: 'text', content: text.slice(last, m.index) });
    segments.push({ type: 'code', content: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ type: 'text', content: text.slice(last) });
  segments.forEach(seg => {
    if (seg.type === 'code') {
      elements.push(<pre key={k++} style={{ background: '#1e1e1e', color: '#d4d4d4', borderRadius: 6, padding: '8px 12px', fontSize: 12, margin: '6px 0 4px', overflowX: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{seg.content.replace(/^\n/, '')}</pre>);
      return;
    }
    const lines = seg.content.split('\n');
    let i = 0;
    while (i < lines.length) {
      const raw = lines[i];
      if (raw.trim() === '') { elements.push(<div key={k++} style={{ height: 6 }} />); i++; continue; }
      const headingMatch = raw.match(/^(#{1,3})\s+(.+)$/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const sizes = { 1: '1.15em', 2: '1.05em', 3: '1em' };
        const weights = { 1: 800, 2: 700, 3: 600 };
        elements.push(<div key={k++} style={{ fontSize: sizes[level], fontWeight: weights[level], margin: '6px 0 2px', lineHeight: 1.3 }}>{parseInlineFormatting(headingMatch[2], k++)}</div>);
        i++; continue;
      }
      if (/^>\s?/.test(raw)) {
        const quoteLines = [];
        while (i < lines.length && /^>\s?/.test(lines[i])) { quoteLines.push(lines[i].replace(/^>\s?/, '')); i++; }
        elements.push(<div key={k++} style={{ borderLeft: '3px solid #14B253', paddingLeft: 10, margin: '4px 0', color: '#555' }}>{quoteLines.map((ql, qi) => (<div key={qi}>{parseInlineFormatting(ql, k++) || <br />}</div>))}</div>);
        continue;
      }
      if (/^(\s*)([-*•])\s+/.test(raw)) {
        const items = [];
        while (i < lines.length && /^(\s*)([-*•])\s+/.test(lines[i])) {
          const indent = lines[i].match(/^(\s*)/)[1].length;
          const txt    = lines[i].replace(/^\s*[-*•]\s+/, '');
          items.push({ txt, indent }); i++;
        }
        elements.push(<ul key={k++} style={{ margin: '4px 0', paddingLeft: 20, listStyleType: 'disc' }}>{items.map((item, ii) => (<li key={ii} style={{ marginLeft: item.indent * 8, marginBottom: 1, lineHeight: 1.5 }}>{parseInlineFormatting(item.txt, k++)}</li>))}</ul>);
        continue;
      }
      if (/^\s*\d+\.\s+/.test(raw)) {
        const items = [];
        while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, '')); i++; }
        elements.push(<ol key={k++} style={{ margin: '4px 0', paddingLeft: 22 }}>{items.map((item, ii) => (<li key={ii} style={{ marginBottom: 1, lineHeight: 1.5 }}>{parseInlineFormatting(item, k++)}</li>))}</ol>);
        continue;
      }
      const leadingSpaces = raw.match(/^(\s*)/)[1];
      const nbsp = leadingSpaces.replace(/ /g, '\u00A0').replace(/\t/g, '\u00A0\u00A0\u00A0\u00A0');
      elements.push(<div key={k++} style={{ lineHeight: 1.5, minHeight: '1.4em' }}>{nbsp && <span>{nbsp}</span>}{parseInlineFormatting(raw.trimStart(), k++)}</div>);
      i++;
    }
  });
  return <>{elements}</>;
};

// ─────────────────────────────────────────────────────────────────────────────
// MENTION HIGHLIGHTER
// ─────────────────────────────────────────────────────────────────────────────
const MessageWithMentions = ({ message: text, mentions = [], currentUserId, members = [] }) => {
  if (!text) return null;
  const myUsernames = new Set(members.filter(m => String(m.id) === String(currentUserId)).map(m => (m.username || '').toLowerCase()));
  const mentionedIds = new Set((mentions || []).map(m => String(m.mentionedEmployeeId)));
  const iAmMentioned = mentionedIds.has(String(currentUserId));
  const mentionRegex = /@([a-zA-Z0-9._]+)/g;
  const parts = []; let last = 0; let match; let k = 0;
  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > last) {
      const segment = text.slice(last, match.index);
      const formatted = parseFormattingSegment(segment, k);
      if (Array.isArray(formatted)) formatted.forEach(f => parts.push(f));
      else parts.push(<span key={k++}>{formatted}</span>);
    }
    const username = match[1].toLowerCase();
    const isMe = myUsernames.has(username) || iAmMentioned;
    parts.push(<Tag key={k++} color={isMe ? 'blue' : 'default'} style={{ margin: '0 1px', fontSize: 13, cursor: 'default', backgroundColor: isMe ? '#e6f7ff' : '#f0f0f0', border: isMe ? '1px solid #91d5ff' : '1px solid #d9d9d9', fontWeight: isMe ? 700 : 500 }}>@{match[1]}</Tag>);
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    const segment = text.slice(last);
    const formatted = parseFormattingSegment(segment, k);
    if (Array.isArray(formatted)) formatted.forEach(f => parts.push(f));
    else parts.push(<span key={k++}>{formatted}</span>);
  }
  return <div style={{ display: 'inline', whiteSpace: 'pre-wrap' }}>{parts}</div>;
};

// ─────────────────────────────────────────────────────────────────────────────
// FORMATTING TOOLBAR
// ─────────────────────────────────────────────────────────────────────────────
const FormattingToolbar = ({ onFormat }) => {
  const buttons = [
    { icon: <BoldOutlined />,          title: 'Bold (Ctrl+B)',          syntax: '**',  type: 'wrap' },
    { icon: <ItalicOutlined />,        title: 'Italic (Ctrl+I)',        syntax: '*',   type: 'wrap' },
    { icon: <StrikethroughOutlined />, title: 'Strikethrough',          syntax: '~~',  type: 'wrap' },
    { icon: <CodeOutlined />,          title: 'Inline Code',            syntax: '`',   type: 'wrap' },
    { icon: <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>{'</>'}</span>, title: 'Code Block',    syntax: '```', type: 'block' },
    { icon: <span style={{ fontSize: 14, lineHeight: 1 }}>•</span>,        title: 'Bullet List',    syntax: '- ',  type: 'list-bullet' },
    { icon: <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}>1.</span>, title: 'Numbered List', syntax: '1. ', type: 'list-number' },
    { icon: <span style={{ fontSize: 14, lineHeight: 1 }}>❝</span>,        title: 'Blockquote',     syntax: '> ',  type: 'quote' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '4px 8px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', borderRadius: '6px 6px 0 0', flexWrap: 'wrap' }}>
      {buttons.map((btn, i) => (
        <Tooltip key={i} title={btn.title} placement="top">
          <Button type="text" size="small" onClick={() => onFormat(btn.syntax, btn.type)}
            style={{ padding: '2px 6px', minWidth: 28, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: 13, borderRadius: 4 }}>
            {btn.icon}
          </Button>
        </Tooltip>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const ProjectChat = ({
  projectData,
  visible,
  onClose,
  currentUserId: propCurrentUserId,
  onMessageSent,
}) => {
  const [messages,               setMessages]               = useState([]);
  const [newMessage,             setNewMessage]             = useState('');
  const [loading,                setLoading]                = useState(false);
  const [showEmojiPicker,        setShowEmojiPicker]        = useState(false);
  const [selectedFiles,          setSelectedFiles]          = useState([]);
  const [replyingTo,             setReplyingTo]             = useState(null);
  const [editingMsgId,           setEditingMsgId]           = useState(null);
  const [openDropdownId,         setOpenDropdownId]         = useState(null);
  const [reactions,              setReactions]              = useState({});
  const [reactionPickerMsgId,    setReactionPickerMsgId]    = useState(null);
  const [chatMembers,            setChatMembers]            = useState([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionSuggestions,     setMentionSuggestions]     = useState([]);
  const [selectedMentionIndex,   setSelectedMentionIndex]   = useState(0);
  const [cursorPosition,         setCursorPosition]         = useState(0);
  const [firstUnreadIndex,       setFirstUnreadIndex]       = useState(null);
  const [mentionBanner,          setMentionBanner]          = useState(null);
  const [showScrollBtn,          setShowScrollBtn]          = useState(false);
  const [newMsgCount,            setNewMsgCount]            = useState(0);
  const [isAtBottom,             setIsAtBottom]             = useState(true);
  const isAtBottomRef = useRef(true);
  const [currentPage,   setCurrentPage]   = useState(0);
  const [hasMore,       setHasMore]       = useState(true);
  const [loadingOlder,  setLoadingOlder]  = useState(false);
  const PAGE_SIZE = 50;

  const lastReadTimestampRef = useRef(null);
  const messagesEndRef       = useRef(null);
  const inputRef             = useRef(null);
  const fileInputRef         = useRef(null);
  const messagesAreaRef      = useRef(null);

  const sessionData   = (() => { try { return JSON.parse(localStorage.getItem('sessionData') || '{}'); } catch { return {}; } })();
  const currentUserId = propCurrentUserId || sessionData?.userId;

  const projectIdRef     = useRef(projectData?.projectId);
  const currentUserIdRef = useRef(currentUserId);
  useEffect(() => { projectIdRef.current     = projectData?.projectId; }, [projectData?.projectId]);
  useEffect(() => { currentUserIdRef.current = currentUserId;          }, [currentUserId]);

  // ─────────────────────────────────────────────────────────
  // LOAD MESSAGES
  // KEY CHANGE: after messages load, calls markProjectChatAsRead
  // so the backend last_read pointer is updated — this makes
  // unread badge counts correct after page refresh.
  // ─────────────────────────────────────────────────────────
  const loadMessages = useCallback(async (projectId) => {
    const pid = projectId || projectIdRef.current;
    if (!pid) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/messages/${pid}`,
        { params: { page: 0, size: PAGE_SIZE } }
      );
      if (response.data.code === 200) {
        const msgs = response.data.data || [];
        setMessages(msgs);
        setCurrentPage(0);
        setHasMore(msgs.length >= PAGE_SIZE);
        lastReadTimestampRef.current = new Date();
        setFirstUnreadIndex(null);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 80);

        // Update backend last_read pointer so badge is correct after refresh
        if (msgs.length > 0 && currentUserIdRef.current) {
          const lastMsg = msgs[msgs.length - 1];
          markProjectChatAsRead(pid, currentUserIdRef.current, lastMsg.messageId);
        }
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      AntMessage.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, []);

  // LOAD OLDER MESSAGES
  const loadOlderMessages = useCallback(async () => {
    const pid = projectIdRef.current;
    if (!pid || loadingOlder || !hasMore) return;
    setLoadingOlder(true);
    const nextPage = currentPage + 1;
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/${pid}`, { params: { page: nextPage, size: PAGE_SIZE } });
      if (response.data.code === 200) {
        const older = response.data.data || [];
        if (older.length === 0) { setHasMore(false); return; }
        const area = messagesAreaRef.current;
        const prevScrollHeight = area ? area.scrollHeight : 0;
        setMessages(prev => [...older, ...prev]);
        setCurrentPage(nextPage);
        setHasMore(older.length >= PAGE_SIZE);
        if (area) requestAnimationFrame(() => { area.scrollTop = area.scrollHeight - prevScrollHeight; });
      }
    } catch (err) { console.error('Failed to load older messages:', err); }
    finally { setLoadingOlder(false); }
  }, [currentPage, hasMore, loadingOlder]);

  // LOAD UNREAD MENTIONS
  const loadUnreadMentions = useCallback(async (projectId) => {
    const uid = currentUserIdRef.current;
    const pid = projectId || projectIdRef.current;
    if (!uid || !pid) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/mentions/unread/${uid}`);
      if (response.data.code === 200) {
        const mentions = response.data.data || [];
        const forThisProject = mentions.filter(m => String(m.projectId) === String(pid));
        if (forThisProject.length > 0) {
          const latest = forThisProject[0];
          setMentionBanner({ visible: true, msg: { messageId: latest.messageId, senderName: latest.mentionedByEmployeeName, message: latest.message }, count: forThisProject.length });
        }
      }
    } catch { /* non-fatal */ }
  }, []);

  // LOAD PARTICIPANTS
  const loadParticipants = useCallback(async (projectId) => {
    const pid = projectId || projectIdRef.current;
    if (!pid) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/participants/${pid}`);
      if (response.data.code === 200) {
        setChatMembers((response.data.data || []).map(p => ({
          id: p.employeeId, name: p.employeeName, avatar: getInitials(p.employeeName),
          role: p.role || 'Team Member', online: p.online || false,
          color: generateColor(p.employeeName), username: p.username, employeeName: p.employeeName,
        })));
      }
    } catch (err) { console.error('Error loading participants:', err); }
  }, []);

  // WEBSOCKET
  useEffect(() => {
    if (!visible || !projectData?.projectId) return;
    const projectId = String(projectData.projectId);
    const handleNewMessage = (data) => {
      if (String(data?.projectId) !== projectId) return;
      const msg = data?.message;
      if (!msg) return;
      setMessages(prev => {
        if (prev.some(m => m.messageId === msg.messageId)) return prev;
        if (!isAtBottomRef.current) { setNewMsgCount(c => c + 1); setShowScrollBtn(true); }
        return [...prev, msg];
      });
      if (String(msg.senderId) !== String(currentUserId)) {
        const isMentioned = Array.isArray(msg.mentions) && msg.mentions.some(m => String(m.mentionedEmployeeId) === String(currentUserId));
        if (isMentioned) { playSound(mentionSound); setMentionBanner({ msg, visible: true, count: 1 }); setTimeout(() => setMentionBanner(null), 6000); }
        else { playSound(notificationSound, 0.3); }
      }
    };
    const handleEditedMessage = (data) => {
      if (String(data?.projectId) !== projectId) return;
      const msg = data?.message;
      if (!msg) return;
      setMessages(prev => prev.map(m => m.messageId === msg.messageId ? { ...m, ...msg } : m));
    };
    const handleDeletedMessage = (data) => {
      if (String(data?.projectId) !== projectId) return;
      const messageId = data?.messageId;
      if (!messageId) return;
      setMessages(prev => prev.filter(m => m.messageId !== messageId));
    };
    const handleParticipantRemoved = (data) => {
      if (String(data?.projectId) !== projectId) return;
      if (String(data?.employeeId) === String(currentUserId)) {
        AntMessage.warning({ content: 'You have been removed from this project chat.', duration: 4, style: { marginTop: '10vh' } });
        setTimeout(() => { if (onClose) onClose(); }, 800);
      } else { loadParticipants(projectId); }
    };
    const unsubNew     = webSocketService.on('chatNewMessage',         handleNewMessage);
    const unsubEdited  = webSocketService.on('chatMessageEdited',      handleEditedMessage);
    const unsubDeleted = webSocketService.on('chatMessageDeleted',     handleDeletedMessage);
    const unsubRemoved = webSocketService.on('chatParticipantRemoved', handleParticipantRemoved);
    return () => { unsubNew(); unsubEdited(); unsubDeleted(); unsubRemoved(); };
  }, [visible, projectData?.projectId, currentUserId]);

  // INIT
  useEffect(() => {
    const pid = projectData?.projectId;
    if (!visible || !pid) return;
    setFirstUnreadIndex(null); setMentionBanner(null); setNewMsgCount(0);
    setShowScrollBtn(false); setMessages([]); setCurrentPage(0); setHasMore(true);
    loadMessages(pid); loadParticipants(pid); loadUnreadMentions(pid);
  }, [visible, projectData?.projectId, loadMessages, loadParticipants, loadUnreadMentions]);

  useEffect(() => {
    if (isAtBottomRef.current) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (visible && inputRef.current) setTimeout(() => inputRef.current.focus(), 100);
  }, [visible]);

  // SCROLL DETECTION
  const handleScroll = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
    if (atBottom) { setShowScrollBtn(false); setNewMsgCount(0); }
    if (el.scrollTop < 60) loadOlderMessages();
  }, [loadOlderMessages]);

  useEffect(() => {
    const el = messagesAreaRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); setShowScrollBtn(false); setNewMsgCount(0); };

  // OUTSIDE CLICK
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker) { const picker = document.querySelector('.EmojiPickerReact'); const btn = event.target.closest('[data-emoji-button]'); if (picker && picker.contains(event.target)) return; if (btn) return; setShowEmojiPicker(false); }
      if (openDropdownId) { const dd = document.querySelector(`[data-dropdown="${openDropdownId}"]`); const trg = document.querySelector(`[data-trigger="${openDropdownId}"]`); if (dd && !dd.contains(event.target) && trg && !trg.contains(event.target)) setOpenDropdownId(null); }
      if (reactionPickerMsgId) { const rp = document.querySelector('[data-reaction-picker]'); if (rp && !rp.contains(event.target)) setReactionPickerMsgId(null); }
      if (showMentionSuggestions) { const mc = document.querySelector('.mention-suggestions'); if (mc && !mc.contains(event.target)) setShowMentionSuggestions(false); }
    };
    if (showEmojiPicker || openDropdownId || showMentionSuggestions || reactionPickerMsgId) setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 100);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker, openDropdownId, showMentionSuggestions]);

  // SEND MESSAGE
  const handleSendMessage = async () => {
    if (!newMessage.trim() && selectedFiles.length === 0) return;
    const messagePayload = { projectId: projectData.projectId, senderId: currentUserId, message: newMessage, messageType: selectedFiles.length > 0 ? 'file' : 'text', replyToMessageId: replyingTo?.messageId || null };
    try {
      let response;
      if (editingMsgId) {
        response = await axios.put(`${API_BASE_URL}/messages/${editingMsgId}`, { message: newMessage }, { params: { currentUserId } });
        if (response.data.code === 200) { setMessages(prev => prev.map(msg => msg.messageId === editingMsgId ? { ...msg, message: newMessage, isEdited: true, editedDate: new Date() } : msg)); setEditingMsgId(null); }
      } else if (selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const sf = selectedFiles[i]; const formData = new FormData();
          formData.append('projectId', projectData.projectId); formData.append('senderId', currentUserId);
          formData.append('message', i === 0 ? newMessage : '');
          if (replyingTo && i === 0) formData.append('replyToMessageId', replyingTo.messageId);
          formData.append('file', sf.file);
          const fileResp = await axios.post(`${API_BASE_URL}/messages/with-file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          if (fileResp.data.code === 200) {
            const saved = fileResp.data.data;
            if (replyingTo && i === 0) saved.replyTo = { messageId: replyingTo.messageId, message: replyingTo.message, senderName: replyingTo.senderName };
            setMessages(prev => prev.some(m => m.messageId === saved.messageId) ? prev : [...prev, saved]);
          }
        }
      } else {
        response = await axios.post(`${API_BASE_URL}/messages`, messagePayload);
        if (response.data.code === 200) {
          const saved = response.data.data;
          if (replyingTo) saved.replyTo = { messageId: replyingTo.messageId, message: replyingTo.message, senderName: replyingTo.senderName };
          setMessages(prev => prev.some(m => m.messageId === saved.messageId) ? prev : [...prev, saved]);
        }
      }
      if (onMessageSent) onMessageSent();
      setNewMessage(''); setSelectedFiles([]); setReplyingTo(null); setShowMentionSuggestions(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || '';
      if (msg.toLowerCase().includes('no longer a participant') || msg.toLowerCase().includes('not a participant')) {
        AntMessage.error({ content: 'You have been removed from this project chat and cannot send messages.', duration: 5 });
      } else { AntMessage.error('Failed to send message'); }
    }
  };

  // DELETE
  const handleDelete = async (msgId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/messages/${msgId}`, { params: { currentUserId } });
      if (response.data.code === 200) { setMessages(prev => prev.filter(msg => msg.messageId !== msgId)); setOpenDropdownId(null); }
    } catch { AntMessage.error('Failed to delete message'); }
  };

  // FORMAT HELPERS
  const applyFormat = (syntax, type) => {
    const ta = inputRef.current?.resizableTextArea?.textArea;
    if (!ta) return;
    const start = ta.selectionStart; const end = ta.selectionEnd;
    const sel = newMessage.slice(start, end); const before = newMessage.slice(0, start); const after = newMessage.slice(end);
    let newVal, newStart, newEnd;
    if (type === 'block') { newVal = before + `\`\`\`\n${sel}\n\`\`\`` + after; newStart = start + 4; newEnd = newStart + sel.length; }
    else if (type === 'list-bullet' || type === 'list-number' || type === 'quote') {
      if (sel.trim()) {
        const prefixed = sel.split('\n').map((line, i) => { if (type === 'list-number') return `${i + 1}. ${line}`; return `${syntax}${line}`; }).join('\n');
        newVal = before + prefixed + after; newStart = start; newEnd = start + prefixed.length;
      } else {
        const lineStart = before.lastIndexOf('\n') + 1; const lineContent = newMessage.slice(lineStart);
        const prefix = type === 'list-number' ? '1. ' : syntax;
        newVal = newMessage.slice(0, lineStart) + prefix + lineContent; newStart = newEnd = lineStart + prefix.length;
      }
    } else { newVal = before + `${syntax}${sel}${syntax}` + after; newStart = start + syntax.length; newEnd = newStart + sel.length; }
    setNewMessage(newVal);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(newStart, newEnd); }, 0);
  };

  // PASTE HANDLER
  const handlePaste = (e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    const html = clipboardData.getData('text/html');
    const plain = clipboardData.getData('text/plain');
    if (!html || html.trim() === '') return;
    e.preventDefault();
    const convert = (node) => {
      if (!node) return '';
      if (node.nodeType === 3) return node.textContent || '';
      const tag = node.tagName?.toLowerCase();
      const children = () => Array.from(node.childNodes).map(convert).join('');
      switch (tag) {
        case 'b': case 'strong': return `**${children()}**`;
        case 'i': case 'em':    return `*${children()}*`;
        case 's': case 'del': case 'strike': return `~~${children()}~~`;
        case 'code': return `\`${children()}\``;
        case 'pre':  return `\`\`\`\n${node.textContent}\n\`\`\``;
        case 'br':   return '\n';
        case 'p':    { const inner = children().trim(); return inner ? inner + '\n' : '\n'; }
        case 'div':  { const inner = children(); return inner.endsWith('\n') ? inner : inner + '\n'; }
        case 'h1':   return `# ${children()}\n`;
        case 'h2':   return `## ${children()}\n`;
        case 'h3': case 'h4': case 'h5': case 'h6': return `### ${children()}\n`;
        case 'blockquote': return children().split('\n').filter(l => l.trim()).map(l => `> ${l}`).join('\n') + '\n';
        case 'ul': return Array.from(node.querySelectorAll(':scope > li')).map(li => `- ${li.textContent.trim()}`).join('\n') + '\n';
        case 'ol': return Array.from(node.querySelectorAll(':scope > li')).map((li, i) => `${i + 1}. ${li.textContent.trim()}`).join('\n') + '\n';
        case 'li':   return children().trim();
        case 'a':    return children();
        case 'img':  return node.alt || '';
        case 'table': { const rows = Array.from(node.querySelectorAll('tr')); return rows.map(row => Array.from(row.querySelectorAll('td,th')).map(cell => cell.textContent.trim()).join('  |  ')).join('\n') + '\n'; }
        case 'thead': case 'tbody': case 'tr': return children();
        case 'td': case 'th': return children() + '  ';
        case 'span': return children();
        default: return children();
      }
    };
    const parser = new DOMParser();
    const doc    = parser.parseFromString(html, 'text/html');
    let converted = convert(doc.body).replace(/\n{3,}/g, '\n\n').trimEnd();
    if (!converted.trim()) converted = plain;
    const ta = inputRef.current?.resizableTextArea?.textArea;
    if (ta) {
      const start = ta.selectionStart; const end = ta.selectionEnd;
      const newVal = newMessage.slice(0, start) + converted + newMessage.slice(end);
      setNewMessage(newVal);
      const newPos = start + converted.length;
      setTimeout(() => { ta.focus(); ta.setSelectionRange(newPos, newPos); }, 0);
    } else { setNewMessage(prev => prev + converted); }
  };

  // KEYDOWN
  const handleKeyDown = (e) => {
    if (e.ctrlKey && !e.shiftKey && e.key === 'b') { e.preventDefault(); applyFormat('**', 'wrap'); return; }
    if (e.ctrlKey && !e.shiftKey && e.key === 'i') { e.preventDefault(); applyFormat('*', 'wrap'); return; }
    if (e.ctrlKey && e.shiftKey  && e.key === 'X') { e.preventDefault(); applyFormat('~~', 'wrap'); return; }
    if (e.ctrlKey && e.shiftKey  && e.key === 'M') { e.preventDefault(); applyFormat('```', 'block'); return; }
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = inputRef.current?.resizableTextArea?.textArea;
      if (ta) { const start = ta.selectionStart; const newVal = newMessage.slice(0, start) + '    ' + newMessage.slice(ta.selectionEnd); setNewMessage(newVal); setTimeout(() => ta.setSelectionRange(start + 4, start + 4), 0); }
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !showMentionSuggestions) {
      const ta = inputRef.current?.resizableTextArea?.textArea;
      if (ta) {
        const pos = ta.selectionStart; const before = newMessage.slice(0, pos); const lastLine = before.split('\n').pop();
        const bulletM = lastLine.match(/^(\s*)([-*•])\s/); const numberedM = lastLine.match(/^(\s*)(\d+)\.\s/);
        if (bulletM) {
          if (lastLine.trim() === bulletM[2]) { e.preventDefault(); setNewMessage(newMessage.slice(0, pos - lastLine.length) + newMessage.slice(pos)); return; }
          e.preventDefault(); const continuation = `\n${bulletM[1]}${bulletM[2]} `; const newVal = before + continuation + newMessage.slice(pos); setNewMessage(newVal);
          setTimeout(() => ta.setSelectionRange(pos + continuation.length, pos + continuation.length), 0); return;
        }
        if (numberedM) {
          if (lastLine.trim() === `${numberedM[2]}.`) { e.preventDefault(); setNewMessage(newMessage.slice(0, pos - lastLine.length) + newMessage.slice(pos)); return; }
          e.preventDefault(); const nextNum = parseInt(numberedM[2], 10) + 1; const continuation = `\n${numberedM[1]}${nextNum}. `; const newVal = before + continuation + newMessage.slice(pos); setNewMessage(newVal);
          setTimeout(() => ta.setSelectionRange(pos + continuation.length, pos + continuation.length), 0); return;
        }
      }
    }
    if (showMentionSuggestions && mentionSuggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); setSelectedMentionIndex(p => p < mentionSuggestions.length - 1 ? p + 1 : 0); break;
        case 'ArrowUp':   e.preventDefault(); setSelectedMentionIndex(p => p > 0 ? p - 1 : mentionSuggestions.length - 1); break;
        case 'Enter':     if (mentionSuggestions[selectedMentionIndex]) { e.preventDefault(); selectMention(mentionSuggestions[selectedMentionIndex]); } break;
        case 'Escape':    setShowMentionSuggestions(false); break;
        default: break;
      }
    } else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  // EMOJI / FILE / MENTION
  const onEmojiClick = (emojiData, event) => { if (event) { event.stopPropagation(); event.preventDefault(); } setNewMessage(prev => prev + (emojiData.emoji || emojiData.native || '')); };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const oversized = files.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) { AntMessage.error(`${oversized.map(f => f.name).join(', ')} exceed 10MB limit`); return; }
    const mapped = files.map(f => ({ fileName: f.name, fileSize: f.size, fileType: f.type, file: f }));
    setSelectedFiles(prev => { const combined = [...prev, ...mapped]; const seen = new Set(); return combined.filter(f => { const key = f.fileName + f.fileSize; if (seen.has(key)) return false; seen.add(key); return true; }); });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const triggerFileInput      = () => fileInputRef.current?.click();
  const removeSelectedFileAt  = (idx) => setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
  const clearAllSelectedFiles = () => { setSelectedFiles([]); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const handleInputChange = (e) => {
    const value = e.target.value; const cursorPos = e.target.selectionStart;
    setNewMessage(value); setCursorPosition(cursorPos);
    const textBefore = value.substring(0, cursorPos); const mentionMatch = textBefore.match(/@(\w*)$/);
    if (mentionMatch) {
      const query = mentionMatch[1].toLowerCase();
      const filtered = chatMembers.filter(m => m.username?.toLowerCase().includes(query) || m.employeeName?.toLowerCase().includes(query));
      setMentionSuggestions(filtered.slice(0, 5)); setShowMentionSuggestions(filtered.length > 0); setSelectedMentionIndex(0);
    } else { setShowMentionSuggestions(false); setMentionSuggestions([]); }
  };

  const selectMention = (member) => {
    const textBefore = newMessage.substring(0, cursorPosition); const textAfter = newMessage.substring(cursorPosition);
    const mentionStart = textBefore.lastIndexOf('@');
    setNewMessage(textBefore.substring(0, mentionStart) + `@${member.username} ` + textAfter);
    setShowMentionSuggestions(false);
    setTimeout(() => {
      if (inputRef.current?.resizableTextArea) {
        const ta = inputRef.current.resizableTextArea.textArea; const newPos = mentionStart + member.username.length + 2;
        ta.focus(); ta.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const scrollToMessage = (messageId) => {
    const el = document.getElementById(`message-${messageId}`);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.style.backgroundColor = 'rgba(37,211,102,0.2)'; setTimeout(() => { el.style.backgroundColor = ''; }, 1500); }
  };

  const handleReply = (msg) => { setReplyingTo(msg); setEditingMsgId(null); setOpenDropdownId(null); inputRef.current?.focus(); };
  const handleEdit  = (msg) => { if (msg.senderId !== currentUserId) return; setNewMessage(msg.message); setEditingMsgId(msg.messageId); setReplyingTo(null); setOpenDropdownId(null); inputRef.current?.focus(); };
  const handleCopy  = (msg) => { if (msg.message) { navigator.clipboard.writeText(msg.message); AntMessage.success('Copied'); } setOpenDropdownId(null); };
  const cancelReply = ()    => setReplyingTo(null);
  const cancelEdit  = ()    => { setEditingMsgId(null); setNewMessage(''); };

  const getMessageMenuItems = (msg) => {
    const isMe = String(msg.senderId) === String(currentUserId); const msgId = msg.messageId || msg.id; const items = [];
    items.push({ key: 'react', label: '😊 React', onClick: () => setReactionPickerMsgId(reactionPickerMsgId === msgId ? null : msgId) });
    items.push({ key: 'reply', label: '↩ Reply', onClick: () => handleReply(msg) });
    if (msg.message?.trim()) items.push({ key: 'copy', label: '📋 Copy', onClick: () => handleCopy(msg) });
    if (isMe) {
      if (msg.message?.trim()) items.push({ key: 'edit', label: '✏️ Edit', onClick: () => handleEdit(msg) });
      items.push({ key: 'delete', label: '🗑 Delete', danger: true, onClick: () => handleDelete(msg.messageId) });
    }
    return items;
  };

  const isMentionedInMessage = (msg) => {
    if (!msg.mentions || !currentUserId) return false;
    return msg.mentions.some(m => String(m.mentionedEmployeeId) === String(currentUserId));
  };

  // REPLY PREVIEW
  const ReplyPreview = ({ replyTo, isMe }) => {
    if (!replyTo) return null;
    return (
      <div onClick={() => scrollToMessage(replyTo.messageId)}
        style={{ background: isMe ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.08)', borderLeft: `3px solid ${isMe ? '#25D366' : '#128C7E'}`, borderRadius: 6, padding: '8px 10px', marginBottom: 6, fontSize: 13, cursor: 'pointer' }}>
        <div style={{ color: isMe ? '#25D366' : '#128C7E', fontWeight: 600, fontSize: 12, marginBottom: 2 }}>{replyTo.senderName}</div>
        <div style={{ color: '#666', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{replyTo.message || '📎 Attachment'}</div>
      </div>
    );
  };

  // REACTIONS
  const REACTION_EMOJIS = ['👍','❤️','😂','😮','😢','🔥','👏','🎉'];
  const toggleReaction = (msgId, emoji) => {
    setReactionPickerMsgId(null);
    setReactions(prev => {
      const msgReactions = { ...(prev[msgId] || {}) }; const users = msgReactions[emoji] ? [...msgReactions[emoji]] : [];
      const myIdx = users.findIndex(u => String(u.userId) === String(currentUserId));
      if (myIdx >= 0) { users.splice(myIdx, 1); }
      else { const me = chatMembers.find(m => String(m.id) === String(currentUserId)); users.push({ userId: currentUserId, userName: me?.name || me?.employeeName || 'You' }); }
      if (users.length === 0) { delete msgReactions[emoji]; } else { msgReactions[emoji] = users; }
      return { ...prev, [msgId]: msgReactions };
    });
  };
  const getReactions = (msgId) => reactions[msgId] || {};

  // RENDER SINGLE MESSAGE
  const renderMessage = (msg, index, allMsgs) => {
    const isMe           = String(msg.senderId) === String(currentUserId);
    const isFirstInGroup = index === 0 || allMsgs[index - 1]?.senderId !== msg.senderId;
    const menuItems      = getMessageMenuItems(msg);
    const mentioned      = isMentionedInMessage(msg);
    const bubbleBg       = isMe ? '#dcf8c6' : mentioned ? '#fff8e1' : '#ffffff';
    const bubbleBorder   = mentioned && !isMe ? '2px solid #FFD700' : 'none';
    return (
      <div key={msg.messageId || msg.id} id={`message-${msg.messageId || msg.id}`} className="message-row"
        style={{ display: 'flex', alignItems: 'flex-start', padding: isFirstInGroup ? '12px 16px 4px 16px' : '2px 16px', gap: 12, flexDirection: isMe ? 'row-reverse' : 'row', position: 'relative', transition: 'background-color 0.3s ease', zIndex: openDropdownId === (msg.messageId || msg.id) ? 9999 : 1, background: mentioned && !isMe ? 'rgba(255,215,0,0.05)' : 'transparent' }}>
        {isFirstInGroup
          ? <Avatar size={32} style={{ backgroundColor: chatMembers.find(m => String(m.id) === String(msg.senderId))?.color || '#1890ff', fontSize: 12, flexShrink: 0 }}>{getInitials(msg.senderName)}</Avatar>
          : <div style={{ width: 32, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', position: 'relative', zIndex: openDropdownId === (msg.messageId || msg.id) ? 9999 : 1 }}>
          {isFirstInGroup && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexDirection: isMe ? 'row-reverse' : 'row' }}>
              <Text strong style={{ fontSize: 13, color: '#262626' }}>{msg.senderName}</Text>
              {mentioned && !isMe && <span style={{ background: '#FFD700', color: '#000', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>@ mentioned you</span>}
            </div>
          )}
          <div className="message-bubble"
            style={{ fontSize: 13, lineHeight: 1.5, color: '#262626', backgroundColor: bubbleBg, border: bubbleBorder, padding: '8px 12px', borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px', boxShadow: mentioned && !isMe ? '0 2px 8px rgba(255,215,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)', maxWidth: 420, wordBreak: 'break-word', position: 'relative', zIndex: openDropdownId === (msg.messageId || msg.id) ? 10000 : 2, paddingBottom: Object.keys(getReactions(msg.messageId || msg.id)).length > 0 ? 18 : 8 }}>
            {(msg.replyTo || msg.replyToMessageId) && (
              <ReplyPreview replyTo={msg.replyTo || { messageId: msg.replyToMessageId, message: msg.replyToMessage, senderName: msg.replyToSenderName }} isMe={isMe} />
            )}
            {(msg.file || msg.fileName) && (
              <div style={{ marginBottom: msg.message?.trim() ? 8 : 0 }}>
                <Attachment file={msg.file || { fileName: msg.fileName, fileSize: msg.fileSize, fileType: msg.fileType, fileUrl: msg.fileUrl }} />
              </div>
            )}
            {msg.message?.trim() && <MessageWithMentions message={msg.message} mentions={msg.mentions} currentUserId={currentUserId} members={chatMembers} />}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, marginTop: 4 }}>
              {msg.isEdited && <Text style={{ fontSize: 10, color: isMe ? 'rgba(0,0,0,0.4)' : '#aaa', fontStyle: 'italic' }}>edited</Text>}
              <Text style={{ fontSize: 10, color: isMe ? 'rgba(0,0,0,0.4)' : '#aaa', whiteSpace: 'nowrap' }}>{msg.timestamp || parseMoment(msg.createdDate).format('h:mm a')}</Text>
            </div>
            {reactionPickerMsgId === (msg.messageId || msg.id) && (
              <div onClick={(e) => e.stopPropagation()} data-reaction-picker
                style={{ position: 'absolute', bottom: '100%', right: isMe ? 0 : 'auto', left: isMe ? 'auto' : 0, marginBottom: 6, zIndex: 10010, background: '#fff', borderRadius: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.18)', border: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 2, padding: '6px 10px' }}>
                {REACTION_EMOJIS.map(emoji => {
                  const msgReactions = getReactions(msg.messageId || msg.id);
                  const alreadyReacted = (msgReactions[emoji] || []).some(u => String(u.userId) === String(currentUserId));
                  return (
                    <button key={emoji} onClick={() => toggleReaction(msg.messageId || msg.id, emoji)}
                      style={{ background: alreadyReacted ? '#f0faf4' : 'transparent', border: alreadyReacted ? '1.5px solid #14B253' : '1.5px solid transparent', borderRadius: '50%', width: 34, height: 34, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.15s, background 0.15s', padding: 0 }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.25)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                      {emoji}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="message-actions" style={{ position: 'absolute', top: 2, right: isMe ? 'auto' : -64, left: isMe ? -64 : 'auto', display: 'flex', alignItems: 'center', gap: 2, opacity: 0, transition: 'opacity 0.15s', zIndex: 10001 }}>
              <button className="action-btn" onClick={(e) => { e.stopPropagation(); const id = msg.messageId || msg.id; setReactionPickerMsgId(reactionPickerMsgId === id ? null : id); }}
                style={{ background: 'rgba(255,255,255,0.95)', border: '1px solid #e0e0e0', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, cursor: 'pointer', padding: 0 }} title="React">😊</button>
              <Button type="text" data-trigger={msg.messageId || msg.id} className="message-menu-trigger"
                onClick={(e) => { e.stopPropagation(); const id = msg.messageId || msg.id; setOpenDropdownId(openDropdownId === id ? null : id); }}
                style={{ background: 'rgba(255,255,255,0.95)', border: '1px solid #e0e0e0', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#666', padding: 0, minWidth: 26, zIndex: 10001 }}>⋯</Button>
            </div>
            {openDropdownId === (msg.messageId || msg.id) && (
              <div data-dropdown={msg.messageId || msg.id}
                style={{ position: 'absolute', top: 30, right: isMe ? 'auto' : -35, left: isMe ? -35 : 'auto', zIndex: 10002, background: '#fff', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', border: '1px solid #e0e0e0', padding: '4px 0', minWidth: 120, pointerEvents: 'auto' }}>
                {menuItems.map((item, i) => (
                  <div key={item.key} onClick={(e) => { e.stopPropagation(); item.onClick(); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: item.danger ? '#ff4d4f' : '#333', borderBottom: i < menuItems.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = item.danger ? '#fff2f0' : '#f5f5f5'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {(() => {
            const msgId = msg.messageId || msg.id; const msgReactions = getReactions(msgId); const entries = Object.entries(msgReactions).filter(([, users]) => users.length > 0);
            if (!entries.length) return null;
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: isMe ? 'flex-end' : 'flex-start', marginTop: -10, paddingLeft: isMe ? 0 : 6, paddingRight: isMe ? 6 : 0, position: 'relative', zIndex: 3 }}>
                {entries.map(([emoji, users]) => {
                  const iReacted = users.some(u => String(u.userId) === String(currentUserId)); const names = users.map(u => u.userName).join(', ');
                  return (
                    <Tooltip key={emoji} title={names} placement="top">
                      <button onClick={() => toggleReaction(msgId, emoji)}
                        style={{ background: iReacted ? '#e6f7ee' : '#fff', border: iReacted ? '1.5px solid #14B253' : '1.5px solid #e0e0e0', borderRadius: 20, padding: '2px 8px', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: iReacted ? 700 : 400, boxShadow: '0 1px 4px rgba(0,0,0,0.12)', color: '#333', transition: 'all 0.15s', lineHeight: 1.4 }}
                        onMouseEnter={e => { e.currentTarget.style.background = iReacted ? '#d0f0de' : '#f5f5f5'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = iReacted ? '#e6f7ee' : '#fff'; e.currentTarget.style.transform = 'scale(1)'; }}>
                        <span style={{ fontSize: 14 }}>{emoji}</span>
                        {users.length > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: iReacted ? '#14B253' : '#888' }}>{users.length}</span>}
                      </button>
                    </Tooltip>
                  );
                })}
                <Tooltip title="Add reaction">
                  <button onClick={(e) => { e.stopPropagation(); setReactionPickerMsgId(reactionPickerMsgId === msgId ? null : msgId); }}
                    style={{ background: '#fff', border: '1.5px dashed #d0d0d0', borderRadius: 20, padding: '2px 7px', fontSize: 13, cursor: 'pointer', color: '#bbb', display: 'inline-flex', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', transition: 'all 0.15s', lineHeight: 1.4 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#14B253'; e.currentTarget.style.color = '#14B253'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#d0d0d0'; e.currentTarget.style.color = '#bbb'; e.currentTarget.style.transform = 'scale(1)'; }}>+</button>
                </Tooltip>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  // DATE HEADER / UNREAD DIVIDER / MENTION BANNER
  const renderDateHeader = (label) => (
    <div style={{ textAlign: 'center', padding: '12px 16px 4px' }}>
      <Divider style={{ margin: 0 }}>
        <span style={{ background: '#e8e8e8', color: '#666', borderRadius: 12, padding: '3px 14px', fontSize: 12, fontWeight: 600 }}>{label}</span>
      </Divider>
    </div>
  );

  const renderUnreadDivider = () => (
    <div style={{ textAlign: 'center', padding: '8px 16px' }}>
      <Divider style={{ margin: 0 }}>
        <span style={{ background: '#1890ff', color: '#fff', borderRadius: 12, padding: '3px 14px', fontSize: 12, fontWeight: 600 }}>New Messages</span>
      </Divider>
    </div>
  );

  const renderMentionBanner = () => {
    if (!mentionBanner?.visible || !mentionBanner?.msg) return null;
    return (
      <div style={{ position: 'absolute', top: 80, left: 16, right: 16, background: '#fff8e1', border: '1px solid #FFD700', borderRadius: 10, padding: '10px 14px', zIndex: 100, boxShadow: '0 4px 16px rgba(255,215,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 20 }}>@</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#614700' }}>{mentionBanner.count > 1 ? `${mentionBanner.count} unread mentions in this project` : `${mentionBanner.msg.senderName} mentioned you`}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mentionBanner.msg.message}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <Button size="small" type="primary" icon={<ArrowDownOutlined />} onClick={() => { scrollToMessage(mentionBanner.msg.messageId); setMentionBanner(null); }} style={{ fontSize: 12, background: '#FFD700', borderColor: '#FFD700', color: '#000' }}>Jump</Button>
          <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setMentionBanner(null)} style={{ color: '#888' }} />
        </div>
      </div>
    );
  };

  const grouped = groupMessagesByDate(messages);

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#fff', position: 'relative' }}>
      {renderMentionBanner()}

      {/* Header */}
      <div style={{ padding: 16, borderBottom: '1px solid #e8e8e8', backgroundColor: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong style={{ fontSize: 16, color: '#262626' }}>
            Project Chat <Badge status="success" style={{ marginLeft: 8 }} title="Real-time" />
          </Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {chatMembers.slice(0, 5).map((member, index) => (
              <Tooltip key={member.id} title={`${member.name} - ${member.role}`}>
                <Badge dot status={member.online ? 'success' : 'default'} offset={[-2, 2]}>
                  <Avatar size={24} style={{ backgroundColor: member.color, fontSize: 10, marginLeft: index > 0 ? -8 : 0, border: '2px solid white', zIndex: chatMembers.length - index }}>{member.avatar}</Avatar>
                </Badge>
              </Tooltip>
            ))}
            {chatMembers.length > 5 && <Avatar size={24} style={{ backgroundColor: '#f0f0f0', color: '#666', fontSize: 10, marginLeft: -8, border: '2px solid white' }}>+{chatMembers.length - 5}</Avatar>}
          </div>
        </div>
        <Text style={{ fontSize: 13, color: '#666' }}>{projectData?.projectName || 'Project'}</Text>
      </div>

      {/* Messages area */}
      <div ref={messagesAreaRef} style={{ flex: 1, overflowY: 'auto', backgroundColor: '#fafafa', position: 'relative', zIndex: 1 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, color: '#666' }}>Loading messages...</div>
        ) : messages.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 200, color: '#bbb', gap: 8 }}>
            <span style={{ fontSize: 36 }}>💬</span>
            <span style={{ fontSize: 14 }}>No messages yet. Start the conversation!</span>
          </div>
        ) : (
          <>
            {loadingOlder && (
              <div style={{ textAlign: 'center', padding: '12px 0', color: '#888', fontSize: 13 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 14, height: 14, border: '2px solid #14B253', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  Loading older messages...
                </span>
              </div>
            )}
            {!loadingOlder && !hasMore && messages.length > 0 && (
              <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
                <span style={{ fontSize: 11, color: '#bbb', background: '#f5f5f5', borderRadius: 10, padding: '3px 12px' }}>Beginning of conversation</span>
              </div>
            )}
            {(() => {
              let msgIndex = 0; const flatMsgs = messages;
              return grouped.map((item, i) => {
                if (item.type === 'date-header') return renderDateHeader(item.label);
                const msg = item.msg; const idx = msgIndex; msgIndex++;
                return (
                  <React.Fragment key={msg.messageId || msg.id}>
                    {firstUnreadIndex !== null && idx === firstUnreadIndex && renderUnreadDivider()}
                    {renderMessage(msg, idx, flatMsgs)}
                  </React.Fragment>
                );
              });
            })()}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {showScrollBtn && (
        <div style={{ position: 'absolute', bottom: editingMsgId || replyingTo || selectedFiles.length > 0 ? 220 : 90, right: 24, zIndex: 50 }}>
          <Button type="primary" shape="round" size="small" icon={<ArrowDownOutlined />} onClick={scrollToBottom}
            style={{ background: '#14B253', borderColor: '#14B253', boxShadow: '0 4px 12px rgba(20,178,83,0.4)', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
            {newMsgCount > 0 ? `${newMsgCount} new` : 'Latest'}
          </Button>
        </div>
      )}

      {/* Reply Bar */}
      {replyingTo && (
        <div style={{ padding: '8px 16px', borderTop: '1px solid #e8e8e8', backgroundColor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#25D366', fontWeight: 600, marginBottom: 2 }}>Replying to {replyingTo.senderName}</div>
            <div style={{ fontSize: 13, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{replyingTo.message || '📎 Attachment'}</div>
          </div>
          <Button type="text" icon={<CloseOutlined />} onClick={cancelReply} style={{ color: '#666', padding: 4, minWidth: 'auto', height: 'auto' }} />
        </div>
      )}

      {/* Edit Bar */}
      {editingMsgId && (
        <div style={{ padding: '8px 16px', borderTop: '1px solid #e8e8e8', backgroundColor: '#fff3cd', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: '#856404', fontWeight: 600 }}>Editing message</div>
          <Button type="text" icon={<CloseOutlined />} onClick={cancelEdit} style={{ color: '#856404', padding: 4, minWidth: 'auto', height: 'auto' }} />
        </div>
      )}

      {/* File Preview */}
      {selectedFiles.length > 0 && (
        <div style={{ padding: '10px 16px 4px', borderTop: '1px solid #e8e8e8', backgroundColor: '#f5f5f5' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: '#888', fontWeight: 500 }}>{selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected</span>
            <Button type="text" size="small" onClick={clearAllSelectedFiles} style={{ fontSize: 11, color: '#ff4d4f', padding: '0 4px', height: 'auto' }}>Clear all</Button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {selectedFiles.map((sf, idx) => {
              const isImg = isImageFile(sf.fileName, sf.fileType);
              const ext   = sf.fileName ? sf.fileName.split('.').pop().toUpperCase() : 'FILE';
              const extColors = { PDF:'#ff4d4f',DOC:'#1890ff',DOCX:'#1890ff',XLS:'#52c41a',XLSX:'#52c41a',PPT:'#fa8c16',PPTX:'#fa8c16',TXT:'#666' };
              const color = extColors[ext] || '#14B253';
              return (
                <div key={idx} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: isImg ? 4 : '6px 10px', maxWidth: 200 }}>
                  {isImg
                    ? <img src={URL.createObjectURL(sf.file)} alt={sf.fileName} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                    : <><div style={{ width: 28, height: 28, borderRadius: 4, background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>{ext.slice(0, 4)}</div><div style={{ minWidth: 0 }}><div style={{ fontSize: 12, fontWeight: 500, color: '#262626', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{sf.fileName}</div><div style={{ fontSize: 10, color: '#888' }}>{formatFileSize(sf.fileSize)}</div></div></>
                  }
                  <button onClick={() => removeSelectedFileAt(idx)} style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#ff4d4f', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, lineHeight: 1, padding: 0 }}>✕</button>
                </div>
              );
            })}
            <button onClick={triggerFileInput} style={{ width: 48, height: 48, borderRadius: 8, border: '1.5px dashed #d9d9d9', background: '#fafafa', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 20, flexShrink: 0 }}>+</button>
          </div>
          <div style={{ fontSize: 11, color: '#bbb', marginTop: 6 }}>Each file will be sent as a separate message</div>
        </div>
      )}

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div style={{ position: 'absolute', bottom: 80, right: 16, zIndex: 10002, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
          <EmojiPicker onEmojiClick={onEmojiClick} autoFocusSearch={false} theme="light" width={300} height={350} previewConfig={{ showPreview: false }} skinTonesDisabled lazyLoadEmojis />
          <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10003 }}>
            <Button type="text" icon={<CloseOutlined />} onClick={(e) => { e.stopPropagation(); setShowEmojiPicker(false); }}
              style={{ background: 'rgba(0,0,0,0.1)', color: '#666', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', fontSize: 12 }} />
          </div>
        </div>
      )}

      {/* Input Area */}
      <div style={{ borderTop: '1px solid #e8e8e8', backgroundColor: '#fff' }}>
        <FormattingToolbar onFormat={applyFormat} />
        <div style={{ padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, border: '1px solid #d9d9d9', borderRadius: '0 0 8px 8px', borderTop: 'none', padding: '8px 12px', backgroundColor: '#fff' }}>
            <Button type="text" icon={<PaperClipOutlined />} size="small" onClick={triggerFileInput} style={{ color: '#666', minWidth: 'auto', padding: 4 }} />
            <div style={{ position: 'relative', flex: 1 }}>
              <Input.TextArea
                ref={inputRef}
                value={newMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={editingMsgId ? 'Edit your message...' : replyingTo ? `Replying to ${replyingTo.senderName}...` : 'Write a message... (@ to mention)'}
                autoSize={{ minRows: 1, maxRows: 5 }}
                bordered={false}
                style={{ fontSize: 13, resize: 'none' }}
                disabled={loading}
              />
              {showMentionSuggestions && mentionSuggestions.length > 0 && (
                <div className="mention-suggestions"
                  style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d9d9d9', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 220, overflowY: 'auto', padding: '4px 0', zIndex: 1000 }}>
                  {mentionSuggestions.map((member, index) => (
                    <div key={member.id} onClick={() => selectMention(member)}
                      style={{ padding: '8px 12px', cursor: 'pointer', backgroundColor: index === selectedMentionIndex ? '#f0faf4' : 'transparent', display: 'flex', alignItems: 'center', gap: 10, transition: 'background 0.15s' }}
                      onMouseEnter={() => setSelectedMentionIndex(index)}>
                      <Avatar size={30} style={{ backgroundColor: member.color, fontSize: 11, flexShrink: 0 }}>{member.avatar}</Avatar>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>{member.employeeName}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>@{member.username} &bull; {member.role}</div>
                      </div>
                      {member.online && <Badge status="success" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button type="text" icon={<SmileOutlined />} size="small" data-emoji-button="true"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowEmojiPicker(!showEmojiPicker); }}
              style={{ color: showEmojiPicker ? '#1890ff' : '#666', minWidth: 'auto', padding: 4, background: showEmojiPicker ? 'rgba(24,144,255,0.1)' : 'transparent' }} />
            <Button type="primary" icon={<SendOutlined />} onClick={handleSendMessage}
              disabled={(!newMessage.trim() && selectedFiles.length === 0) || loading} loading={loading}
              style={{ minWidth: 'auto', padding: '4px 8px', backgroundColor: editingMsgId ? '#fa8c16' : '#52c41a', borderColor: editingMsgId ? '#fa8c16' : '#52c41a' }} />
          </div>
        </div>
      </div>

      <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }}
        multiple accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx" />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .message-row:hover .message-actions { opacity: 1 !important; }
        .message-row:hover .message-menu-trigger { opacity: 1 !important; }
        .action-btn:hover { background: rgba(0,0,0,0.08) !important; }
        .message-bubble { transition: all 0.2s ease; }
        .message-bubble:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important; }
        .message-menu-trigger:hover { background: rgba(0,0,0,0.1) !important; color: #1890ff !important; }
        [data-dropdown] { z-index: 10002 !important; position: absolute !important; }
        .message-menu-trigger { z-index: 10001 !important; }
        .message-row { isolation: isolate; }
        .message-bubble { transform: translateZ(0); }
        .mention-suggestions::-webkit-scrollbar { width: 4px; }
        .mention-suggestions::-webkit-scrollbar-thumb { background: #d9d9d9; border-radius: 4px; }
      `}</style>
    </div>
  );
};

export default ProjectChat;